import { ref, computed, watch, toValue, type MaybeRefOrGetter } from 'vue'
import { supabase } from '../lib/supabase'
import { useToast } from './useToast'
import { useAuthStore } from '../stores/auth'
import { NOTITIE_ENTITY_FK, type Notitie, type NotitieInsert, type NotitieEntityType } from '../../../../types'

/** Entities the portal (opdrachtgever) can attach notes to. */
export type PortalNotitieEntityType = Extract<NotitieEntityType, 'rapport' | 'offerte' | 'offerte_bundel'>

export interface NotitieFormInput {
  inhoud: string
  reminder_at?: string | null
}

/**
 * Notities CRUD for the portal, via direct Supabase (RLS-scoped). An opdrachtgever sees
 * notes flagged visible-to-portal on entities they own, plus their own notes, and can
 * author/edit/delete their own notes. Authorship/scope columns are stamped server-side.
 */
export function useNotities(
  entityType: PortalNotitieEntityType,
  entityIdSource: MaybeRefOrGetter<string | null | undefined>,
) {
  const toast = useToast()
  const authStore = useAuthStore()
  const fk = NOTITIE_ENTITY_FK[entityType]

  const notities = ref<Notitie[]>([])
  const loading = ref(false)

  const currentUserId = computed(() => authStore.user?.id ?? null)
  const isOwn = (notitie: Notitie) => !!currentUserId.value && notitie.created_by === currentUserId.value

  async function fetchNotities() {
    const entityId = toValue(entityIdSource)
    if (!entityId) {
      notities.value = []
      return
    }

    loading.value = true
    try {
      const { data, error } = await supabase
        .from('notities')
        .select('*')
        .eq(fk, entityId)
        .order('created_at', { ascending: false })

      if (error) throw error
      notities.value = data ?? []
    } catch (err) {
      console.error('Failed to load notities:', err)
      toast.error('Notities laden mislukt')
    } finally {
      loading.value = false
    }
  }

  async function createNotitie(input: NotitieFormInput): Promise<Notitie | null> {
    const entityId = toValue(entityIdSource)
    if (!entityId) throw new Error('Geen entity id beschikbaar voor notitie')

    try {
      const payload: NotitieInsert = {
        inhoud: input.inhoud.trim(),
        reminder_at: input.reminder_at ?? null,
      }
      ;(payload as Record<string, unknown>)[fk] = entityId

      const { data, error } = await supabase.from('notities').insert(payload).select().single()
      if (error) throw error
      if (data) notities.value = [data, ...notities.value]
      toast.success('Notitie toegevoegd')
      return data ?? null
    } catch (err) {
      console.error('Failed to create notitie:', err)
      toast.error('Notitie opslaan mislukt')
      throw err
    }
  }

  async function updateNotitie(id: string, updates: NotitieFormInput): Promise<Notitie | null> {
    try {
      const { data, error } = await supabase
        .from('notities')
        .update({ inhoud: updates.inhoud.trim(), reminder_at: updates.reminder_at ?? null })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      if (data) notities.value = notities.value.map((notitie) => (notitie.id === id ? data : notitie))
      toast.success('Notitie bijgewerkt')
      return data ?? null
    } catch (err) {
      console.error('Failed to update notitie:', err)
      toast.error('Notitie bijwerken mislukt')
      throw err
    }
  }

  async function deleteNotitie(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('notities').delete().eq('id', id)
      if (error) throw error
      notities.value = notities.value.filter((notitie) => notitie.id !== id)
      toast.success('Notitie verwijderd')
    } catch (err) {
      console.error('Failed to delete notitie:', err)
      toast.error('Notitie verwijderen mislukt')
      throw err
    }
  }

  watch(
    () => toValue(entityIdSource),
    () => {
      void fetchNotities()
    },
    { immediate: true },
  )

  return { notities, loading, currentUserId, isOwn, fetchNotities, createNotitie, updateNotitie, deleteNotitie }
}
